import { useEffect, type ReactNode } from 'react';
import { useToast } from '@paige/ui';
import { ApiError } from '../api/http';

type Listener = (sentence: string) => void;

const listeners = new Set<Listener>();

/**
 * A write refused because the view is read-only (403 `support_grant_read_only`, round 3 of the
 * customer page's critique): the server's own sentence, said once, wherever the request came from.
 * The query client's shared failure path calls this (main.tsx); anything else is ignored.
 */
export function noteReadOnlyRefusal(e: unknown): void {
  if (!(e instanceof ApiError) || e.code !== 'support_grant_read_only') return;
  const sentence = e.detail ?? e.message;
  for (const listener of listeners) listener(sentence);
}

/** Says a read-only refusal as a toast, at most once every two seconds; mounted beside the company's screens. */
export function ReadOnlyRefusals(): ReactNode {
  const { toast } = useToast();
  useEffect(() => {
    let last = 0;
    const say: Listener = (sentence) => {
      const now = Date.now();
      if (now - last < 2_000) return;
      last = now;
      toast({ title: sentence, tone: 'warning' });
    };
    listeners.add(say);
    return () => {
      listeners.delete(say);
    };
  }, [toast]);
  return null;
}
